import type { FC, ReactNode } from "react"
import React, { useMemo } from "react"
import { isAddress } from "@ethersproject/address"
import { Stack, Text, Flex, Box, Badge } from "@chakra-ui/react"
import truncate from "truncate"
import { isMobile } from "react-device-detect"

import type { Account, Organization } from "@/query/graphql"
import type { AvatarSize } from "@/common/components/UserAvatar"
import UserAvatar from "@/common/components/UserAvatar"
import AddressTooltip from "@/common/components/AddressTooltip"
import Link from "@/common/components/Link"
import { ROUTES } from "@/common/constants/routes"
import { shortSolAddress, shortString } from "@/common/helpers/string"
import { useDevice } from "@/common/hooks/useDevice"
import { getWhiteLabelRoute } from "@/whitelabel/utils/breadcrumb"
import { getDisplayName } from "@/user/helpers/user"
import CopyButton from "@/common/components/CopyButton"
import { isSolanaAddress } from "@/web3/helpers/address"
import { isMultiChainTokenIds } from "@/organization/helpers/organization"

type SmallDelegateUserIdentityProps = {
  index?: number
  account: Pick<Account, "name" | "picture" | "address" | "twitter">
  spacing?: number
  hasFormStyle?: boolean
  hasCopyButton?: boolean
  isGnosisSafe?: boolean
  votes?: string
  tokenSymbol?: string
  isWhiteLabel: boolean
  displayProfileLink?: boolean
  organization: Pick<Organization, "slug" | "tokenIds">
  avatarSize?: AvatarSize
}

const SmallDelegateUserIdentity: FC<SmallDelegateUserIdentityProps> = ({
  account,
  spacing = 3,
  hasFormStyle = false,
  hasCopyButton = false,
  votes,
  tokenSymbol,
  isWhiteLabel,
  displayProfileLink = true,
  avatarSize = "md",
  organization,
}) => {
  const { address, picture, twitter } = account

  const displayName = getDisplayName(account)
  const hasTwitterUsername = Boolean(twitter)

  const route = isWhiteLabel
    ? getWhiteLabelRoute(
        ROUTES.governance.delegate.profile(organization.slug, address),
      )
    : ROUTES.governance.delegate.profile(organization.slug, address)

  const isMultiChain = organization
    ? isMultiChainTokenIds(organization.tokenIds)
    : false

  return (
    <Stack
      isInline
      align="center"
      alignItems={votes ? "flex-start" : "center"}
      bgColor={hasFormStyle ? "gray.50" : undefined}
      border={hasFormStyle ? "gray.dark" : undefined}
      borderRadius={hasFormStyle ? 6 : undefined}
      p={hasFormStyle ? "2" : undefined}
      spacing={spacing}
      w="full"
    >
      <UserAvatar
        address={address}
        isVerifiedByTwitter={hasTwitterUsername}
        shouldShowBadge={false}
        size={avatarSize}
        src={picture}
      />
      <ProfileDetails
        address={address}
        badge={
          isMultiChain && isSolanaAddress(address) ? (
            <Badge
              borderRadius="md"
              color="gray.800"
              colorScheme="gray"
              fontWeight="semibold"
            >
              SOL
            </Badge>
          ) : null
        }
        displayName={displayName}
        hasCopyButton={hasCopyButton}
        profileUrl={displayProfileLink ? route : undefined}
        tokenSymbol={tokenSymbol}
        votes={votes}
      />
    </Stack>
  )
}

type ProfileDetailsProps = {
  address: string
  displayName: string
  hasCopyButton: boolean
  votes: string | undefined
  tokenSymbol: string | undefined
  profileUrl?: string
  badge?: ReactNode
}
const ProfileDetails: FC<ProfileDetailsProps> = ({
  address,
  displayName,
  hasCopyButton,
  votes,
  tokenSymbol,
  profileUrl,
  badge,
}) => {
  const { onLargeDevice } = useDevice()

  const maxLengthTruncate = useMemo(() => {
    if (Boolean(badge)) {
      return isMobile ? 14 : 16
    }

    return 20
  }, [badge])

  return (
    <Flex alignItems="baseline" flexDirection="column">
      <Flex align="center" direction="row" justifyContent="top">
        {profileUrl ? (
          <Link href={profileUrl}>
            <Text
              color="gray.600"
              display={onLargeDevice}
              fontSize="sm"
              fontWeight="semibold"
              noOfLines={1}
            >
              <AddressTooltip aria-label="Complete address" label={address}>
                {isSolanaAddress(displayName) && !isAddress(displayName)
                  ? shortSolAddress(displayName)
                  : isAddress(displayName) && !isSolanaAddress(displayName)
                  ? shortString(displayName)
                  : truncate(displayName, maxLengthTruncate)}
              </AddressTooltip>
            </Text>
          </Link>
        ) : (
          <Text
            color="gray.600"
            display={onLargeDevice}
            fontSize="sm"
            fontWeight="semibold"
            noOfLines={1}
          >
            <AddressTooltip aria-label="Complete address" label={address}>
              {isSolanaAddress(displayName) && !isAddress(displayName)
                ? shortSolAddress(displayName)
                : isAddress(displayName) && !isSolanaAddress(displayName)
                ? shortString(displayName)
                : truncate(displayName, maxLengthTruncate)}
            </AddressTooltip>
          </Text>
        )}

        {badge ? (
          <Box mb={0.5} ml={2}>
            {badge}
          </Box>
        ) : null}
      </Flex>

      {hasCopyButton ? (
        <CopyButton size={8} tooltipLabel="address" value={address} />
      ) : null}
      {votes && (
        <Text fontSize="2xs" fontWeight="thin" mt={1}>
          {votes} {tokenSymbol}
        </Text>
      )}
    </Flex>
  )
}

export default SmallDelegateUserIdentity
